// 🩺 클라이언트 오류 표본 수집 (2026-08-22 유지보수 개선) — "됐다 안 됐다" 간헐 버그를 사용자 보고
//   대신 기록으로 진단한다. uncaught error / unhandledrejection 을 표본으로 모아 30초마다 배치 전송.
//   개인정보 설계(보수적): 오류 메시지·스택·화면 경로(쿼리스트링 제거)만 — 대화 내용·토큰은 수집 자체를
//   안 하고, 긴 영숫자열(토큰류)은 클라에서도 마스킹(서버도 이중 방어). 로그인 전 오류는 보내지 않는다.
//   수집량: 세션당 고유 오류 30종 캡 + 같은 오류는 횟수만 증가(dedup) + 서버측 20건/시간 상한.
import { useAuthStore } from '../stores/authStore';
import { isTauri } from '../desktop';
import { isAndroidApp } from '../utils/platform';

interface ErrItem { kind: string; msg: string; stack: string; path: string; count: number; ver: string; platform: string }

const MAX_DISTINCT = 30;          // 세션당 고유 오류 종수 캡
const FLUSH_MS = 30_000;
const items = new Map<string, ErrItem>();   // dedupKey → item (count 누적)
let dirty = false;
let installed = false;
let sending = false;

const scrub = (s: string, max: number) => String(s || '').replace(/[A-Za-z0-9_-]{40,}/g, '«redacted»').slice(0, max);
const platform = () => (isTauri() ? 'tauri' : (isAndroidApp() ? 'apk' : 'web'));
const pathNow = () => { try { return location.pathname + location.hash.split('?')[0]; } catch { return ''; } };

function record(kind: string, msg: string, stack: string): void {
	try {
		// 자기 자신(전송 실패 등)發 오류는 무시 — 무한 루프 방지
		if (/errorReport|client-errors/.test(msg + stack)) return;
		const m = scrub(msg, 500);
		if (!m.trim()) return;   // 메시지 없는 리소스 로드 오류 등 — 노이즈 제외
		const key = `${kind}|${m.slice(0, 200)}`;
		const cur = items.get(key);
		if (cur) { cur.count = Math.min(999, cur.count + 1); dirty = true; return; }
		if (items.size >= MAX_DISTINCT) return;   // 캡 초과 — 새 종류는 이 세션에선 드랍
		items.set(key, {
			kind, msg: m, stack: scrub(stack, 2000), path: scrub(pathNow(), 200), count: 1,
			ver: (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?'), platform: platform(),
		});
		dirty = true;
	} catch { /* 수집기는 절대 앱을 방해하지 않는다 */ }
}

async function flush(): Promise<void> {
	if (!dirty || sending) return;
	try {
		const { authToken, userId } = useAuthStore.getState();
		if (!authToken || !userId) return;   // 로그인 전 — 다음 주기에 재시도
		const batch = [...items.values()].map((it) => ({ ...it }));
		if (batch.length === 0) return;
		sending = true;
		const r = await fetch('/api/tpt/client-errors', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'X-Auth-Token': authToken, 'X-User-Id': userId },
			body: JSON.stringify({ items: batch.slice(0, 10) }),
			keepalive: true,
		});
		// 성공(또는 429=상한)한 표본은 비운다 — 같은 오류가 계속 나면 새로 기록돼 다시 잡힌다
		if (r.ok || r.status === 429) { items.clear(); dirty = false; }
	} catch { /* 네트워크 실패 — 다음 주기에 재시도 */ } finally { sending = false; }
}

/** 🩺 일회성 원격 진단 리포트 (2026-08-31) — 오류가 아닌 실측 수치를 같은 채널(tpt_client_errors)로.
 *  재현이 안 되는 기기 의존 레이아웃 버그(앱 대화방 하단 탭바 잘림)를 사용자 폰의 실제 수치로 진단한다.
 *  개인정보 없음(순수 수치·경로), dedup·캡·마스킹은 record 가 동일 적용. 진단 종료 후 호출부를 제거할 것. */
export function reportDiag(tag: string, msg: string): void {
	record(`diag:${tag}`, msg, '');
}

/** 전역 오류 수집 설치 — main.tsx 에서 1회. 실패해도 앱 동작에 영향 없음. */
export function installErrorReporter(): void {
	if (installed || typeof window === 'undefined') return;
	installed = true;
	window.addEventListener('error', (e) => {
		// 스크립트 오류만 — 리소스(img/script) 로드 실패는 error 객체가 없어 제외(노이즈·개인정보 無가치)
		if (!(e as ErrorEvent).error && !(e as ErrorEvent).message) return;
		record('error', String((e as ErrorEvent).message || ''), String((e as ErrorEvent).error?.stack || ''));
	});
	window.addEventListener('unhandledrejection', (e) => {
		const r = (e as PromiseRejectionEvent).reason as { message?: string; stack?: string } | string | undefined;
		record('unhandledrejection', String((typeof r === 'object' && r?.message) || r || ''), String((typeof r === 'object' && r?.stack) || ''));
	});
	window.setInterval(() => void flush(), FLUSH_MS);
	// 탭 닫힘/숨김 직전 마지막 전송 시도 (keepalive fetch)
	document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flush(); });
}
